import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@material-ui/core";
import AddGappsSourceDialog from "../Buttons/GappsSourceDialog";
import EditLdapSourceDialog from "../Buttons/LdapSourceDialog";
import Office365SourceDialog from "../Buttons/Office365SourceDialog";

import { Source } from "@dashboard-v3/api";
import { GappsSettings, LdapSettings, Office365Settings } from "../types";

type Params = {
  source: Source;
};

export default function EditSource({ source }: Params) {
  const { t } = useTranslation("accounts");
  const [dialogOpen, setDialogOpen] = useState(false);
  const type = source.configuration.type;

  const closeDialog = () => setDialogOpen(false);

  return (
    <>
      <Button
        data-testid="accounts__edit-source-btn"
        color="primary"
        size="medium"
        variant="text"
        disableElevation
        onClick={() => setDialogOpen(true)}
      >
        {t("common:edit")}
      </Button>

      {type === "OFFICE365" && dialogOpen && (
        <Office365SourceDialog
          isOpen={dialogOpen}
          onClose={closeDialog}
          isAuthorized={true}
          settings={getEditableSettings<Office365Settings>(source)}
          id={source.id}
        />
      )}

      {type === "GAPPS" && dialogOpen && (
        <AddGappsSourceDialog
          isOpen={dialogOpen}
          onClose={closeDialog}
          settings={getEditableSettings<GappsSettings>(source)}
          id={source.id}
        />
      )}

      {type === "LDAP" && (
        <EditLdapSourceDialog
          isOpen={dialogOpen}
          onClose={closeDialog}
          settings={getEditableSettings<LdapSettings>(source)}
          id={source.id}
        />
      )}
    </>
  );
}

function getEditableSettings<T>(source: Source) {
  const { type, syncScheduleTime } = source.configuration;
  let settings = { ...(syncScheduleTime && { syncScheduleTime }) };

  switch (type) {
    case "GAPPS":
      const { gappsSettings } = source.configuration;
      settings = { ...settings, ...gappsSettings };
      break;
    case "OFFICE365":
      const { office365Settings } = source.configuration;
      settings = { ...settings, ...office365Settings };
      break;
    case "LDAP":
      const { ldapSettings } = source.configuration;
      settings = { ...settings, ...ldapSettings };
      break;
    default:
      throw new Error(`Unsupported source type: ${type}`);
  }

  return settings as T;
}
